import { NavLink, useLocation } from "react-router-dom";
import { Home, FileText, Building, FolderOpen, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLoan } from "@/context/LoanContext";
import { TabKey } from "@/lib/lifecycle";
import { useVisibleTabs } from "@/hooks/useVisibleTabs";
import { isNavPathActive } from "@/lib/navActive";
import { useEConsentGateStatus } from "@/hooks/useEConsentGateStatus";
import { useState, useEffect } from "react";

const bottomNavItems = [
  { key: "dashboard", label: "Dashboard", shortLabel: "Dashboard", icon: Home, to: "/" },
  { key: "application", label: "Application", shortLabel: "Application", icon: FileText, to: "/application" },
  { key: "myloan", label: "My Loan", shortLabel: "My Loan", icon: Building, to: "/my-loan" },
  { key: "documents", label: "Documents", shortLabel: "Documents", icon: FolderOpen, to: "/documents" },
];

export const MobileBottomNav = () => {
  const { state } = useLoan();
  // E-consent hard gate (ticket pending, 2026-07-23): no navigation until accepted.
  const { consented } = useEConsentGateStatus();
  // NP-408: flag-aware — Income Verification/Conditions hidden for v1.0.
  const visibleTabKeys = useVisibleTabs(state);
  const loc = useLocation();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // Hide bottom nav when virtual keyboard is open
  useEffect(() => {
    const handleViewportChange = () => {
      if (window.visualViewport) {
        const keyboardOpen = window.visualViewport.height < window.innerHeight * 0.75;
        setIsKeyboardOpen(keyboardOpen);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      return () => window.visualViewport?.removeEventListener('resize', handleViewportChange);
    }
  }, []);

  // Filter items to only show those that are visible for current loan state
  const availableItems = bottomNavItems.filter(item => 
    visibleTabKeys.includes(item.key as TabKey)
  );

  // Only show on mobile screens and hide when keyboard is open
  if (!consented) return null;

  if (isKeyboardOpen) {
    return null;
  }

  return (
    <nav 
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-card/95 backdrop-blur-md border-t border-border shadow-lg transition-transform duration-200"
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-center justify-around px-2 py-2 safe-area-pb">
        {availableItems.map((item) => {
          const Icon = item.icon;
          const active = isNavPathActive(item.to, loc.pathname);

          return (
            <NavLink
              key={item.key}
              to={item.to}
              className={cn(
                "flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg min-h-[60px] min-w-[60px] touch-manipulation transition-colors",
                active
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
              data-testid={`bottom-nav-${item.key}`}
            >
              <Icon className="size-5" />
              <span className="text-xs font-medium leading-none">
                {item.shortLabel}
              </span>
            </NavLink>
          );
        })}
        
        {/* More button if there are additional tabs */}
        {visibleTabKeys.length > availableItems.length && (
          <button 
            className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg min-h-[60px] min-w-[60px] touch-manipulation transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
            data-testid="bottom-nav-more"
          >
            <MoreHorizontal className="size-5" />
            <span className="text-xs font-medium leading-none">More</span>
          </button>
        )}
      </div>
    </nav>
  );
};